import { gql } from '@apollo/client';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { withMetrics } from 'client/data/api/api-metrics';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { EdmundsAPI } from 'client/data/api/api-client';
import { VisitorModel } from 'client/data/models/visitor';
import { getSupportedGraphqlModelYear } from './vehicle-model-year';

function buildStyleModelYearPath(styleId) {
  return styleId ? `styles["${styleId}"].modelYear` : null;
}

function buildMaintenanceParamsPath({ makeSlug, modelSlug, year, styleId }) {
  return `makes["${makeSlug}"].models["${modelSlug}"].years["${year}"].styles["${styleId}"].maintenanceParams`;
}

function buildMaintenanceCountPath({ makeSlug, modelSlug, year, styleId }) {
  return `makes["${makeSlug}"].models["${modelSlug}"].years["${year}"].styles["${styleId}"].count`;
}

function buildMaintenanceDetailsPath({ makeSlug, modelSlug, year, styleId, mileage }) {
  return `makes["${makeSlug}"].models["${modelSlug}"].years["${year}"].styles["${styleId}"].details.mileage["${mileage}"]`;
}

function buildMaintenanceRecallsPath(modelYearId) {
  return `modelYearIds["${modelYearId}"].recalls`;
}

export const MaintenancePaths = {
  buildStyleModelYearPath,
  buildMaintenanceParamsPath,
  buildMaintenanceCountPath,
  buildMaintenanceDetailsPath,
  buildMaintenanceRecallsPath,
};

const MaintenanceAction = PropTypes.shape({
  id: PropTypes.number,
  action: PropTypes.string,
  item: PropTypes.string,
  itemDescription: PropTypes.string,
  laborUnits: PropTypes.number,
  note1: PropTypes.string,
  note2: PropTypes.string,
  partCostPerUnit: PropTypes.number,
  partUnits: PropTypes.number,
});

const ServiceType = PropTypes.shape({
  mileage: PropTypes.number,
  name: PropTypes.string,
});

const MileageRanges = PropTypes.arrayOf(
  PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
    representativeMileage: PropTypes.number,
  })
);

export const MaintenanceEntities = {
  StyleModelYear: PropTypes.shape({
    name: PropTypes.string,
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    submodelSlug: PropTypes.string,
    year: PropTypes.number,
  }),
  MileageRanges,
  ServiceType,
  MaintenanceDetails: PropTypes.shape({
    maintenanceActionsWithPartCost: PropTypes.arrayOf(MaintenanceAction),
    orderedMaintenanceActions: PropTypes.arrayOf(MaintenanceAction),
    mileageRanges: MileageRanges,
    serviceMiles: PropTypes.arrayOf(PropTypes.number),
    nearestServiceInterval: PropTypes.number,
    serviceTypes: PropTypes.arrayOf(ServiceType),
    currentServiceType: ServiceType,
    maintenanceActionCount: PropTypes.number,
    zipLaborRate: PropTypes.number,
    discountRate: PropTypes.number,
  }),
  Recall: PropTypes.shape({
    id: PropTypes.number,
    recallNumber: PropTypes.string,
    manufacturedFrom: PropTypes.string,
    manufacturedTo: PropTypes.string,
    componentDescription: PropTypes.string,
    manufacturerRecallNumber: PropTypes.string,
    numberOfVehiclesAffected: PropTypes.string,
    defectConsequence: PropTypes.string,
    defectCorrectiveAction: PropTypes.string,
    defectDescription: PropTypes.string,
  }),
};

export const MaintenanceModel = createModelSegment('maintenance', [
  /**
   * NOTE: Move to vehicle-v2 in case need to use anywhere outside of maintenance
   * @see buildStyleModelYearPath
   */
  {
    path: 'styles["{styleId}"].modelYear',
    resolve({ styleId }, context) {
      return withMetrics(EdmundsAPI, context)
        .fetchJson(
          `/vehicle/v3/styles?id=${styleId}&fields=makeName,modelName,makeNiceId,modelNiceId,year,name,subModels`
        )
        .then(({ results }) => ({
          name: get(results, '[0].name'),
          makeName: get(results, '[0].makeName'),
          modelName: get(results, '[0].modelName'),
          makeSlug: get(results, '[0].makeNiceId'),
          modelSlug: get(results, '[0].modelNiceId'),
          submodelSlug: get(results, '[0].subModels[0].niceId'),
          year: get(results, '[0].year'),
        }))
        .catch(() => null);
    },
  },
  /**
   * @see buildMaintenanceParamsPath
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].years["{year}"].styles["{styleId}"].maintenanceParams',
    resolve({ makeSlug, modelSlug, year, styleId }, context) {
      const modelYear = getSupportedGraphqlModelYear(year);
      if (modelYear === null) {
        return Promise.resolve({});
      }

      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($makeSlug: String!, $modelSlug: String!, $year: Int!, $styleId: String!) {
              makes(makeSlug: $makeSlug) {
                id
              }
              modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, year: $year) {
                id
              }
              style(styleId: $styleId) {
                engine {
                  engineCode
                }
                transmission {
                  transmissionTypeCode
                }
              }
            }
          `,
          {
            makeSlug,
            modelSlug,
            year: modelYear,
            styleId: styleId.toString(),
          }
        )
        .then(({ makes, modelYears, style }) => ({
          makeId: get(makes, '[0].id'),
          modelYearId: get(modelYears, '[0].id'),
          engineCode: get(style, 'engine.engineCode'),
          transmissionType: get(style, 'transmission.transmissionTypeCode'),
        }))
        .catch(() => ({}));
    },
  },
  /**
   * @see buildMaintenanceCountPath
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].years["{year}"].styles["{styleId}"].count',
    resolve({ makeSlug, modelSlug, year, styleId }, context) {
      return context
        .resolveValue(buildMaintenanceParamsPath({ makeSlug, modelSlug, year, styleId }))
        .then(({ modelYearId, engineCode, transmissionType }) => {
          if (!(modelYearId && engineCode && transmissionType)) {
            return 0;
          }

          return withMetrics(EdmundsAPI, context)
            .fetchJson(
              `/maintenance/v2/count?modelyearid=${modelYearId}&enginecode=${engineCode}&transmissioncode=${transmissionType}`
            )
            .then(response => get(response, 'maintenance', 0));
        });
    },
  },
  /**
   * @see buildMaintenanceDetailsPath
   */
  {
    path: 'makes["{makeSlug}"].models["{modelSlug}"].years["{year}"].styles["{styleId}"].details.mileage["{mileage}"]',
    async resolve({ makeSlug, modelSlug, year, styleId, mileage }, context) {
      const [{ makeId, modelYearId, engineCode, transmissionType }, { zipCode }] = await Promise.all([
        context.resolveValue(buildMaintenanceParamsPath({ makeSlug, modelSlug, year, styleId })),
        context.resolveValue('location', VisitorModel),
      ]);

      if (!(modelYearId && engineCode && transmissionType)) {
        return Promise.resolve({});
      }

      return withMetrics(EdmundsAPI, context)
        .fetchJson(
          `/maintenance/v2/details?makeid=${makeId}&zipcode=${zipCode}&modelyearid=${modelYearId}&enginecode=${engineCode}&styleid=${styleId}&transmissioncode=${transmissionType}&mileage=${mileage}`
        )
        .catch(() => ({}));
    },
  },
  /**
   * @see buildMaintenanceRecallsPath
   */
  {
    path: 'modelYearIds["{modelYearId}"].recalls',
    async resolve({ modelYearId }, context) {
      return withMetrics(EdmundsAPI, context)
        .fetchJson(`/maintenance/v2/recalls?modelyearid=${modelYearId}`)
        .then(response => response?.recallHolder)
        .catch(() => null);
    },
  },
]);
